import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { getModelsBbox } from '@viewer/section-box/sectionMath'
import type { LocalViewpoint } from '@viewer/lib/viewpointsStore'
import { lerVisualizacaoDaCena } from '@modules/bcf/lib/bcfReferencial'
import {
  camaraDoViewpoint,
  centroDaCaixa,
  chaveDaAncora,
  pontoAFrenteDaCamara,
  type Ponto,
} from './pinAncora'

// A âncora de um pin calculada na cena (ver pinAncora.ts para as
// regras). Fica em cache, em memória, por tópico, viewpoint e modelos
// abertos: não se grava no viewpoint nem no ficheiro BCF, porque depende
// dos modelos que estão abertos agora.
//
// Só leituras, e as mais leves: um GlobalId para localId por modelo
// (`getLocalIdsByGuids`) e as caixas (`getBoxes`). Nada de realce, de
// visibilidade nem de materiais. Não se usa o `resolveGuidTargets` do
// "Ver": ele pede ao worker a lista inteira dos itens com geometria em
// cada chamada (para expandir zonas e sistemas), o que com dezenas de
// tópicos e vários modelos enchia o worker do Fragments. Quem chama
// (BcfPinsOverlay) só calcula com os modelos estáveis: nenhum a abrir,
// a recuperação da sessão decidida e o conjunto de modelos parado.

const cache = new Map<string, Ponto | null>()

/** Esquece as âncoras calculadas (testes, ou para forçar o recálculo). */
export function limparAncorasCalculadas(): void {
  cache.clear()
}

/** O centro da caixa dos elementos seleccionados no viewpoint, procurados
 *  em todos os modelos abertos, ou null quando nenhum está aberto (ou não
 *  tem geometria: uma zona ou um sistema cai para a câmara). */
async function centroDosElementos(guids: readonly string[]): Promise<Ponto | null> {
  if (guids.length === 0) return null
  const alvos: { modelId: string; localId: number }[] = []
  for (const m of useAppStore.getState().models) {
    const model = viewerApi.getModel(m.id)
    if (!model) continue
    const ids = await model.getLocalIdsByGuids([...guids]).catch(() => [] as (number | null)[])
    for (const localId of ids) if (typeof localId === 'number') alvos.push({ modelId: m.id, localId })
  }
  if (alvos.length === 0) return null
  const caixa = await viewerApi.getItemsWorldBox(alvos)
  return caixa ? centroDaCaixa(caixa) : null
}

/** Calcula a âncora, sem cache. */
export async function calcularAncora(vp: Pick<LocalViewpoint, 'viewpoint'>): Promise<Ponto | null> {
  const v = vp.viewpoint
  if (v.so_imagem) return null
  const doElementos = await centroDosElementos(v.components?.selected_global_ids ?? [])
  if (doElementos) return doElementos
  // A câmara relida como no "Ver": a regra comum de referencial e, se
  // não cair junto dos modelos, o referencial de cada modelo
  // (bcfReferencial.ts). Nunca lança.
  const camera = camaraDoViewpoint(v, (xml) => lerVisualizacaoDaCena(xml, v.model_refs ?? [])?.lida.camera ?? null)
  if (!camera) return null
  const caixa = getModelsBbox()
  return pontoAFrenteDaCamara(camera, caixa ? { min: caixa.min, max: caixa.max } : null)
}

/**
 * A âncora de um pin: a do viewpoint, se a tiver; senão a calculada, da
 * cache ou calculada agora. Null quando não há posição possível (imagem,
 * elementos fora dos modelos abertos e sem câmara útil).
 */
export async function ancoraDoPin(
  topicGuid: string,
  vp: Pick<LocalViewpoint, 'id' | 'viewpoint' | 'anchor'>,
): Promise<Ponto | null> {
  if (vp.anchor) return vp.anchor
  const chave = chaveDaAncora(topicGuid, vp.id, useAppStore.getState().models)
  if (cache.has(chave)) return cache.get(chave) ?? null
  const p = await calcularAncora(vp)
  cache.set(chave, p)
  return p
}
